import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { City } from '../../../models/City';
import { CitySuggestion } from '../../../models/CitySuggestion';
import { UtilsService } from '../../../services/utils.service';

@Component({
  selector: 'app-city-autocomplete',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './city-autocomplete.component.html',
  styleUrl: './city-autocomplete.component.scss',
  // Registra i controlli ngModel interni nel form (NgForm) del componente genitore.
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class CityAutocompleteComponent {
  @Input() city?: string;
  @Input() province?: string;
  @Input() postalCode?: string;
  @Input() cityId?: number;
  @Input() required: boolean = true;
  @Input() idPrefix: string = 'city';
  @Input() showProvince: boolean = true;
  @Input() showPostalCode: boolean = true;

  @Output() cityChange = new EventEmitter<string | undefined>();
  @Output() provinceChange = new EventEmitter<string | undefined>();
  @Output() postalCodeChange = new EventEmitter<string | undefined>();
  @Output() cityIdChange = new EventEmitter<number | undefined>();

  suggestions: CitySuggestion[] = [];
  capOptions: string[] = [];

  constructor(private utilsService: UtilsService) {}

  get effectiveCapOptions(): string[] {
    if (this.postalCode && !this.capOptions.includes(this.postalCode)) {
      return [this.postalCode, ...this.capOptions];
    }
    return this.capOptions;
  }

  onCityInput(value: string): void {
    this.city = value;
    this.cityChange.emit(value);

    // Digitando una nuova città, provincia e CAP si azzerano finché non si seleziona una voce.
    this.province = undefined;
    this.provinceChange.emit(undefined);
    this.postalCode = undefined;
    this.postalCodeChange.emit(undefined);
    this.cityId = undefined;
    this.cityIdChange.emit(undefined);
    this.capOptions = [];

    if (value && value.trim().length >= 3) {
      this.utilsService.findCityByName(value.trim()).subscribe({
        next: (cities) => {
          this.suggestions = this.groupCities(cities);
        },
        error: () => {
          this.suggestions = [];
        },
      });
    } else {
      this.suggestions = [];
    }
  }

  selectSuggestion(suggestion: CitySuggestion): void {
    this.city = suggestion.name;
    this.cityChange.emit(suggestion.name);

    this.cityId = suggestion.id;
    this.cityIdChange.emit(suggestion.id);

    this.province = suggestion.provinceAbbr;
    this.provinceChange.emit(suggestion.provinceAbbr);

    this.capOptions = suggestion.postalCodes;
    if (suggestion.postalCodes.length === 1) {
      this.postalCode = suggestion.postalCodes[0];
      this.postalCodeChange.emit(this.postalCode);
    } else {
      this.postalCode = undefined;
      this.postalCodeChange.emit(undefined);
    }

    this.suggestions = [];
  }

  onPostalCodeChange(value: string): void {
    this.postalCode = value;
    this.postalCodeChange.emit(value);
  }

  private groupCities(cities: City[]): CitySuggestion[] {
    const map = new Map<string, CitySuggestion>();
    for (const c of cities) {
      if (!c.name) continue;
      const key = c.name + '|' + (c.provinceAbbr ?? '');
      let suggestion = map.get(key);
      if (!suggestion) {
        // id rappresentativo = prima riga (primo CAP) trovata per quella città.
        suggestion = { id: c.id, name: c.name, provinceAbbr: c.provinceAbbr ?? '', postalCodes: [] };
        map.set(key, suggestion);
      }
      if (c.postalCode && !suggestion.postalCodes.includes(c.postalCode)) {
        suggestion.postalCodes.push(c.postalCode);
      }
    }
    const list = Array.from(map.values());
    list.forEach((s) => s.postalCodes.sort());
    return list;
  }
}
